/**
 * Связь страницы Lens с личным расширением «Lens · Alfa People»
 * (`extensions/alfa-people`). Расширение кладёт на страницы Lens
 * content-скрипт, страница и скрипт обмениваются сообщениями через
 * `window.postMessage`. ID расширения знать не нужно: ответит тот скрипт,
 * который установлен.
 *
 * Протокол (поле `lensAlfaPeople` отличает наши сообщения от чужих):
 *   страница → { lensAlfaPeople: 'request', id, action: 'ping' | 'fetch', managers? }
 *   скрипт   → { lensAlfaPeople: 'progress', id, text }
 *   скрипт   → { lensAlfaPeople: 'response', id, ok, version?, exports?, error? }
 */

export interface AlfaPeopleExport {
  cpId: string;
  data: unknown;
}

interface BridgeResponse {
  lensAlfaPeople: 'response';
  id: string;
  ok: boolean;
  version?: string;
  exports?: AlfaPeopleExport[];
  error?: string;
}

function call(
  action: 'ping' | 'fetch',
  extra: Record<string, unknown>,
  timeoutMs: number,
  onProgress?: (text: string) => void,
): Promise<BridgeResponse> {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('timeout'));
    }, timeoutMs);
    function onMessage(ev: MessageEvent) {
      if (ev.source !== window || ev.origin !== window.location.origin) return;
      const msg = ev.data as { lensAlfaPeople?: string; id?: string; text?: string } | null;
      if (!msg || msg.id !== id) return;
      if (msg.lensAlfaPeople === 'progress') {
        onProgress?.(String(msg.text ?? ''));
        return;
      }
      if (msg.lensAlfaPeople !== 'response') return;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(msg as BridgeResponse);
    }
    window.addEventListener('message', onMessage);
    window.postMessage({ lensAlfaPeople: 'request', id, action, ...extra }, window.location.origin);
  });
}

/** Версия установленного расширения или null, если его нет. */
export async function pingAlfaPeopleExtension(): Promise<string | null> {
  try {
    const r = await call('ping', {}, 800);
    return r.ok ? (r.version ?? '?') : null;
  } catch {
    return null;
  }
}

/**
 * Забирает выгрузку по каждому руководителю. Если портал просит войти,
 * расширение открывает вкладку портала и ждёт до 5 минут — отсюда таймаут.
 */
export async function fetchAlfaPeopleExports(
  managers: string[],
  onProgress?: (text: string) => void,
): Promise<AlfaPeopleExport[]> {
  let r: BridgeResponse;
  try {
    r = await call('fetch', { managers }, 7 * 60_000, onProgress);
  } catch {
    throw new Error('Расширение не ответило. Проверьте, что оно включено, и обновите страницу.');
  }
  if (!r.ok) throw new Error(r.error || 'Расширение не смогло забрать выгрузку');
  return r.exports ?? [];
}
